import { Button } from '@heidi/design-system/button';
import { TypographyCaptionBold } from '@heidi/design-system/typography';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';

import { HEIDI_2_AWARENESS_BANNER_MODAL_ID } from '../../../contracts/heidi-2';
import {
  getHeidi2Ports,
  heidi2OnboardingOpenAtom,
  heidi2OptInModalOpenAtom,
} from '../../../data/heidi-2';
import { Heidi2RequestAccessModal } from '../request-access-modal/heidi-2-request-access-modal';

import { Heidi2RequestAccessBanner } from './heidi-2-request-access-banner';

import type { Heidi2AwarenessBannerPorts } from './heidi-2-awareness-banner-ports';
import { Heidi2ExploreLaterBanner } from './heidi-2-explore-later-banner';
import { Heidi2OnboardingHost } from './heidi-2-onboarding-host';
import { Heidi2OnboardingRelaunch } from './heidi-2-onboarding-relaunch';
import { Heidi2OptInModal } from './heidi-2-opt-in-modal';
import {
  type Heidi2WelcomeCohort,
  useHeidi2WelcomeCohort,
} from './use-heidi-2-welcome-cohort';

/**
 * Routes where a mount is a boot in flight rather than a landing.
 * `/scribe/new-user/*` is onboarding and `/scribe` is the redirect route that
 * decides where the boot lands; both render `ScribeLayout`, so this surface
 * mounts there and is then torn down by the redirect.
 *
 * For the tour that teardown is unrecoverable: `Heidi2OnboardingFlow` accepts
 * on mount, so the host has already POSTed `activate-heidi-2`, and it records
 * nothing as seen until `onFinish`, so the next mount reads Heidi 2 and the
 * cohort gate refuses it. A clinician finishing onboarding got one frame of
 * full-screen tour and never saw it again.
 */
const isBootRoute = (pathname: string) =>
  pathname === '/scribe' || pathname.startsWith('/scribe/new-user');

const bannerMessage = defineMessage({
  defaultMessage: 'Heidi II is here to handle tasks for you.',
  id: 'YInl+1Jpsg',
  description: 'Message in the banner announcing the Heidi II early-access opt-in',
});

const dismissLabelMessage = defineMessage({
  defaultMessage: 'Dismiss Heidi II banner',
  id: 'alQaaxrM3h',
  description: 'Accessible label for dismissing the Heidi II awareness banner',
});

const earlyAccessMessage = defineMessage({
  defaultMessage: 'Get early access',
  id: 'NZ1lq41PY3',
  description: 'Button in the Heidi II awareness banner that opens the opt-in modal',
});

const CloudHeidi2AwarenessBanner = ({ ports }: { ports: Heidi2AwarenessBannerPorts }) => {
  const intl = useIntl();
  const appPorts = getHeidi2Ports();
  const pathname = appPorts.usePathname();
  const isHeidi2OnboardingEnabled = appPorts.useIsHeidi2OnboardingEnabled();
  const cohort = useHeidi2WelcomeCohort();
  const markModalAsViewed = appPorts.useMarkModalAsViewed();
  const setOptInModalOpen = useSetAtom(heidi2OptInModalOpenAtom);
  const setOnboardingOpen = useSetAtom(heidi2OnboardingOpenAtom);
  const [isHiddenLocally, setIsHiddenLocally] = useState(false);
  // Activation flips billing to Heidi 2 before the tour ends; keep the host
  // mounted until onFinished so Home landing and seen persistence still run.
  // Held as the cohort it opened for: on a team, that flip also reads as
  // `activated`, and the run must not change mode part-way through.
  const [heldCohort, setHeldCohort] = useState<Heidi2WelcomeCohort | null>(null);
  const viewTrackedRef = useRef(false);

  const { data: modalView } = useQueryUser(
    (user) => user?.seen_modals?.[HEIDI_2_AWARENESS_BANNER_MODAL_ID]
  );
  const isDismissed = appPorts.isModalClosed(
    HEIDI_2_AWARENESS_BANNER_MODAL_ID,
    modalView
  );
  const trackButtonClicked = appPorts.trackButtonClicked;
  const trackBannerViewed = appPorts.trackHeidi2AwarenessBannerViewed;

  const handleEarlyAccess = useCallback(() => {
    trackButtonClicked({
      screen_name: ScreenName.Heidi2AwarenessBanner,
      action: ButtonAction.Heidi2AwarenessBannerEarlyAccess,
      label: intl.formatMessage(earlyAccessMessage),
    });
    markModalAsViewed({
      modal_id: HEIDI_2_AWARENESS_BANNER_MODAL_ID,
      increment_count: true,
      action: ModalViewAction.engage,
    });
    setOptInModalOpen(true);
  }, [intl, markModalAsViewed, setOptInModalOpen, trackButtonClicked]);

  const handleDismiss = useCallback(() => {
    trackButtonClicked({
      screen_name: ScreenName.Heidi2AwarenessBanner,
      action: ButtonAction.Heidi2AwarenessBannerDismiss,
      label: intl.formatMessage(dismissLabelMessage),
    });
    setIsHiddenLocally(true);
    markModalAsViewed({
      modal_id: HEIDI_2_AWARENESS_BANNER_MODAL_ID,
      increment_count: false,
      action: ModalViewAction.dismiss,
    });
  }, [intl, markModalAsViewed, trackButtonClicked]);

  const isVisible =
    cohort !== null && !isDismissed && !isHiddenLocally && !isBootRoute(pathname);
  // With onboarding on, this banner hands over to the onboarding host instead.
  const isStripVisible = appPorts.useClaimTopStrip(
    'heidi-2-awareness',
    isVisible && !isHeidi2OnboardingEnabled
  );
  appPorts.useReportTopStrip(isStripVisible);

  // Publish as soon as the first-run takeover is due (or already held), so
  // queued surfaces like Session Merge drop out before the host paints.
  // Only write while open — a false write would clear a concurrent relaunch.
  const isFirstRunTakeoverOpen =
    isHeidi2OnboardingEnabled && !isHiddenLocally && (isVisible || heldCohort !== null);

  useEffect(() => {
    if (!isFirstRunTakeoverOpen) return undefined;
    setOnboardingOpen(true);
    return () => setOnboardingOpen(false);
  }, [isFirstRunTakeoverOpen, setOnboardingOpen]);

  useEffect(() => {
    if (!isStripVisible || viewTrackedRef.current) return;

    viewTrackedRef.current = true;
    trackBannerViewed();
  }, [isStripVisible, trackBannerViewed]);

  if (isHeidi2OnboardingEnabled && isVisible && heldCohort === null) {
    setHeldCohort(cohort);
  }

  if (isHeidi2OnboardingEnabled) {
    if (isHiddenLocally || heldCohort === null) return null;
    return (
      <Heidi2OnboardingHost
        ports={ports}
        isActivated={heldCohort === 'activated'}
        onFinished={() => {
          setHeldCohort(null);
          setIsHiddenLocally(true);
        }}
      />
    );
  }

  return (
    <>
      {isStripVisible ? (
        <section
          className="relative flex h-9 w-full shrink-0 items-center justify-center gap-3 bg-entity-yellow-muted p-1 text-entity-yellow-foreground"
          data-testid="heidi-2-awareness-banner"
        >
          <TypographyCaptionBold>
            <FormattedMessage {...bannerMessage} />
          </TypographyCaptionBold>
          <Button
            type="button"
            variant="surface"
            size="xs"
            onClick={handleEarlyAccess}
            data-testid="heidi-2-awareness-banner-early-access"
          >
            <FormattedMessage {...earlyAccessMessage} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            iconOnly
            aria-label={intl.formatMessage(dismissLabelMessage)}
            className="absolute end-1.5"
            onClick={handleDismiss}
            data-testid="heidi-2-awareness-banner-dismiss"
          >
            <X />
          </Button>
        </section>
      ) : null}
      <Heidi2OptInModal />
    </>
  );
};

/**
 * In-flow T4 chrome (not queued). Applies R1/R2 itself; R4 via `seen_modals`.
 * Wrapper skips SaaS billing/allowance queries on-prem.
 *
 * The early-access banner is for Heidi 1 admins. The request-access banner is
 * for Heidi 1 non-admins. The explore-later banner and the tour relaunch are
 * for Heidi II, so at most one banner shows at a time.
 */
export const Heidi2AwarenessBanner = ({
  ports,
}: {
  ports: Heidi2AwarenessBannerPorts;
}) => {
  const { isOnPrem, isAppleCompliantBuild } = getHeidi2Ports();
  return isOnPrem || isAppleCompliantBuild() ? null : (
    <>
      <Heidi2RequestAccessModal />
      <CloudHeidi2AwarenessBanner ports={ports} />
      <Heidi2RequestAccessBanner />
      <Heidi2ExploreLaterBanner />
      <Heidi2OnboardingRelaunch ports={ports} />
    </>
  );
};
